import React from 'react';
import BaseChart from './BaseChart';

/**
 * ExecutionsPerWeekChart Component
 *
 * Displays executions per week using a line chart with:
 * - Weekly execution trends (total executions)
 * - A/B test executions line
 * - Time-based x-axis
 * - Interactive tooltips (shared across both lines)
 * - Responsive design
 * - Legend to distinguish series
 *
 * Expected data format (from API):
 * {
 *   "success": true,
 *   "weekly_executions": [
 *     {
 *       "week_start": "2025-10-27",
 *       "executions": 72,
 *       "experiment_executions": 24  // A/B test executions
 *     },
 *     ...
 *   ],
 *   "total_executions": 136,
 *   "total_experiment_executions": 24,
 *   "period": "30d"
 * }
 */
const ExecutionsPerWeekChart = ({ data, period, loading = false }) => {
  if (
    loading ||
    !data ||
    !data.weekly_executions ||
    data.weekly_executions.length === 0
  ) {
    return (
      <BaseChart
        title={`Executions Per Week (${period || 'N/A'})`}
        subtitle="Weekly execution trends"
        type="line"
        series={[]}
        height={350}
        loading={loading}
      />
    );
  }

  const chartOptions = {
    stroke: {
      curve: 'smooth',
      width: 3,
    },
    colors: ['#1976d2', '#f57c00'], // Blue for total executions, Orange for A/B test
    legend: {
      show: true,
      position: 'bottom',
      horizontalAlign: 'center',
      fontSize: '12px',
      markers: {
        width: 12,
        height: 12,
        radius: 6,
      },
      offsetY: 0,
      itemMargin: {
        horizontal: 15,
        vertical: 5,
      },
    },
    xaxis: {
      type: 'datetime',
      labels: {
        style: {
          fontSize: '11px',
        },
        datetimeFormatter: {
          year: 'yyyy',
          month: 'MMM',
          day: 'dd',
          hour: 'HH:mm',
        },
        formatter: function (val) {
          // Convert timestamp to readable date format
          const date = new Date(val);
          const month = date.toLocaleDateString('en-US', { month: 'short' });
          const day = date.getDate().toString().padStart(2, '0');
          return `${month} ${day}`;
        },
        rotate: -45,
      },
      title: {
        text: 'Week Starting',
        style: {
          fontSize: '12px',
          fontWeight: 600,
        },
      },
    },
    yaxis: {
      min: 0,
      max: undefined, // Let ApexCharts auto-calculate
      labels: {
        style: {
          fontSize: '11px',
        },
        formatter: function (val) {
          return val ? val.toLocaleString() : '0';
        },
      },
      title: {
        text: 'Executions',
        style: {
          fontSize: '12px',
          fontWeight: 600,
        },
      },
      forceNiceScale: true,
      tickAmount: 6,
    },
    tooltip: {
      enabled: true,
      shared: true, // Show all series in tooltip
      followCursor: true,
      intersect: false,
      x: {
        format: 'MMM dd, yyyy',
      },
      y: {
        formatter: function (val) {
          return val ? val.toLocaleString() + ' executions' : '0 executions';
        },
      },
      style: {
        fontSize: '12px',
      },
    },
    markers: {
      size: 6,
      colors: ['#1976d2', '#f57c00'],
      strokeColors: '#fff',
      strokeWidth: 2,
      hover: {
        size: 8,
      },
    },
    grid: {
      show: true,
      borderColor: '#e0e0e0',
      strokeDashArray: 3,
      position: 'back',
      xaxis: {
        lines: {
          show: false,
        },
      },
      yaxis: {
        lines: {
          show: true,
        },
      },
    },
    responsive: [
      {
        breakpoint: 768,
        options: {
          chart: {
            height: 350,
          },
          xaxis: {
            labels: {
              style: {
                fontSize: '9px',
              },
            },
          },
        },
      },
    ],
  };

  // Prepare series data - using latest API format with experiment_executions
  const series = [
    {
      name: 'Total Executions',
      data: data.weekly_executions.map(week => ({
        x: new Date(week.week_start).getTime(),
        y: week.executions || 0,
      })),
    },
    {
      name: 'A/B Test',
      data: data.weekly_executions.map(week => ({
        x: new Date(week.week_start).getTime(),
        y: week.experiment_executions || 0, // New field from API
      })),
    },
  ];

  return (
    <BaseChart
      title={`Executions Per Week (${period || 'N/A'})`}
      subtitle="Weekly execution trends"
      type="line"
      series={series}
      options={chartOptions}
      height={350}
      loading={loading}
    />
  );
};

export default ExecutionsPerWeekChart;
